import { useState, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button, Dropdown, DropdownChip, DropdownContent, DropdownTrigger } from '@mesh.ai/rabbit';

import HierarchicalTreeSelector from 'uiToolkitV1/HierarchicalTreeSelector/HierarchicalTreeSelector';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

interface OrgNode {
  id: string;
  name: string;
  type: 'company' | 'division' | 'bu' | 'department';
  children: OrgNode[];
}

const MOCK_DATA: OrgNode[] = [
  {
    id: 'company1',
    name: 'Company 1',
    type: 'company',
    children: [
      {
        id: 'division1_c1',
        name: 'Division 1',
        type: 'division',
        children: [],
      },
      {
        id: 'division2_c1',
        name: 'Division 2',
        type: 'division',
        children: [
          {
            id: 'bu1_c1',
            name: 'BU 1',
            type: 'bu',
            children: [],
          },
          {
            id: 'bu2_c1',
            name: 'BU 2',
            type: 'bu',
            children: [
              {
                id: 'dept1_c1',
                name: 'Department 1',
                type: 'department',
                children: [],
              },
              {
                id: 'dept2_c1',
                name: 'Department 2',
                type: 'department',
                children: [],
              },
              {
                id: 'dept3_c1',
                name: 'Department 3',
                type: 'department',
                children: [],
              },
            ],
          },
          {
            id: 'bu3_c1',
            name: 'BU 3',
            type: 'bu',
            children: [],
          },
        ],
      },
      {
        id: 'division3_c1',
        name: 'Division 3',
        type: 'division',
        children: [],
      },
    ],
  },
  {
    id: 'company2',
    name: 'Company 2',
    type: 'company',
    children: [
      {
        id: 'division1_c2',
        name: 'Division 1',
        type: 'division',
        children: [],
      },
      {
        id: 'division2_c2',
        name: 'Division 2',
        type: 'division',
        children: [
          {
            id: 'bu1_c2',
            name: 'BU 1',
            type: 'bu',
            children: [],
          },
          {
            id: 'bu2_c2',
            name: 'BU 2',
            type: 'bu',
            children: [
              {
                id: 'dept1_c2',
                name: 'Department 1',
                type: 'department',
                children: [],
              },
              {
                id: 'dept2_c2',
                name: 'Department 2',
                type: 'department',
                children: [],
              },
              {
                id: 'dept3_c2',
                name: 'Department 3',
                type: 'department',
                children: [],
              },
            ],
          },
          {
            id: 'bu3_c2',
            name: 'BU 3',
            type: 'bu',
            children: [],
          },
        ],
      },
      {
        id: 'division3_c2',
        name: 'Division 3',
        type: 'division',
        children: [],
      },
    ],
  },
  {
    id: 'company3',
    name: 'Company 3',
    type: 'company',
    children: [
      {
        id: 'division1_c3',
        name: 'Division 1',
        type: 'division',
        children: [],
      },
      {
        id: 'division2_c3',
        name: 'Division 2',
        type: 'division',
        children: [
          {
            id: 'bu1_c3',
            name: 'BU 1',
            type: 'bu',
            children: [],
          },
          {
            id: 'bu2_c3',
            name: 'BU 2',
            type: 'bu',
            children: [
              {
                id: 'dept1_c3',
                name: 'Department 1',
                type: 'department',
                children: [],
              },
              {
                id: 'dept2_c3',
                name: 'Department 2',
                type: 'department',
                children: [],
              },
              {
                id: 'dept3_c3',
                name: 'Department 3',
                type: 'department',
                children: [],
              },
            ],
          },
          {
            id: 'bu3_c3',
            name: 'BU 3',
            type: 'bu',
            children: [],
          },
        ],
      },
      {
        id: 'division3_c3',
        name: 'Division 3',
        type: 'division',
        children: [],
      },
    ],
  },
  {
    id: 'company4',
    name: 'Company 4',
    type: 'company',
    children: [
      {
        id: 'division1_c4',
        name: 'Division 1',
        type: 'division',
        children: [],
      },
      {
        id: 'division2_c4',
        name: 'Division 2',
        type: 'division',
        children: [
          {
            id: 'bu1_c4',
            name: 'BU 1',
            type: 'bu',
            children: [],
          },
          {
            id: 'bu2_c4',
            name: 'BU 2',
            type: 'bu',
            children: [
              {
                id: 'dept1_c4',
                name: 'Department 1',
                type: 'department',
                children: [],
              },
              {
                id: 'dept2_c4',
                name: 'Department 2',
                type: 'department',
                children: [],
              },
              {
                id: 'dept3_c4',
                name: 'Department 3',
                type: 'department',
                children: [],
              },
            ],
          },
          {
            id: 'bu3_c4',
            name: 'BU 3',
            type: 'bu',
            children: [],
          },
        ],
      },
      {
        id: 'division3_c4',
        name: 'Division 3',
        type: 'division',
        children: [],
      },
    ],
  },
];

// Function to collect all node IDs from the tree
const getAllNodeIds = (nodes: OrgNode[]): string[] => {
  const ids: string[] = [];

  const traverse = (node: OrgNode) => {
    ids.push(node.id);
    if (node.children) {
      node.children.forEach(traverse);
    }
  };

  nodes.forEach(traverse);
  return ids;
};

export const CompensationOrgDropdown = () => {
  const allNodeIds = useMemo(() => getAllNodeIds(MOCK_DATA), []);

  const [appliedNodes, setAppliedNodes] = useState<Set<string>>(new Set(allNodeIds)); // Committed state - all selected by default
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set(allNodeIds)); // Draft state - all selected by default
  const [isOpen, setIsOpen] = useState(false);
  const maxDisplayItems = 2;

  // Function to calculate how many descendants are selected within each company
  const calculateSelectionsByCompany = useMemo(() => {
    const counts = new Map<string, number>();

    const countSelectedInTree = (node: OrgNode): number => {
      let count = selectedNodes.has(node.id) ? 1 : 0;

      if (node.children) {
        node.children.forEach((child: OrgNode) => {
          count += countSelectedInTree(child);
        });
      }

      return count;
    };

    MOCK_DATA.forEach((company: OrgNode) => {
      let companyCount = 0;

      // Count selected nodes in this company's children (excluding the company itself)
      if (company.children) {
        company.children.forEach((child: OrgNode) => {
          companyCount += countSelectedInTree(child);
        });
      }

      // Also include the company itself if it's selected
      if (selectedNodes.has(company.id)) {
        companyCount += 1;
      }

      counts.set(company.name, companyCount);
    });

    return counts;
  }, [selectedNodes]); // Changed back to selectedNodes for real-time updates

  const handleApply = () => {
    setAppliedNodes(new Set(selectedNodes));
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedNodes(new Set(appliedNodes));
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // When opening, sync selectedNodes with appliedNodes
      setSelectedNodes(new Set(appliedNodes));
    }
  };

  const getDisplayContent = () => {
    const filteredEntries = Array.from(calculateSelectionsByCompany.entries()).filter(([, value]) => value > 0);

    if (filteredEntries.length === 0) {
      return (
        <TextBodyMedium bold color="text.g3">
          None
        </TextBodyMedium>
      );
    }

    if (filteredEntries.length > maxDisplayItems) {
      const visibleItems = filteredEntries
        .slice(0, maxDisplayItems)
        .map(([key, value]) => `${key} (${value})`)
        .join(', ');
      const remainingCount = filteredEntries.length - maxDisplayItems;

      return (
        <>
          <TextBodyMedium bold color="text.g1" userSelect="none">
            {visibleItems},
          </TextBodyMedium>
          <TextBodyMedium bold color="#0D819F" userSelect="none">
            {` +${remainingCount}`}
          </TextBodyMedium>
        </>
      );
    }

    const displayText = filteredEntries.map(([key, value]) => `${key} (${value})`).join(', ');

    return (
      <TextBodyMedium bold color="text.g1" userSelect="none">
        {displayText}
      </TextBodyMedium>
    );
  };

  return (
    <Dropdown open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownTrigger asChild>
        <DropdownChip>
          <TextBodyMedium bold color="text.g2">
            Org hierarchy :
          </TextBodyMedium>
          {getDisplayContent()}
        </DropdownChip>
      </DropdownTrigger>
      <DropdownContent align="start" style={{ width: '320px', padding: '0px' }}>
        <Flex flexDir="column" h="100%" w="100%">
          <Box p="8px 8px 0px 8px">
            <HierarchicalTreeSelector
              data={MOCK_DATA}
              selectedNodes={selectedNodes}
              onSelectionChange={setSelectedNodes}
            />
          </Box>
          <Flex
            justify="space-between"
            align="center"
            h="72px"
            px="16px"
            bg="surface.F5F7F9"
            boxShadow="0px -1px 0px 0px #CFD7DF"
          >
            <Button variant="transparentOutline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="filled" onClick={handleApply}>
              Apply
            </Button>
          </Flex>
        </Flex>
      </DropdownContent>
    </Dropdown>
  );
};
